import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { Plus, Edit, Trash2, Settings, Shield, Search, AlertTriangle, CheckCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { getRules, createRule, updateRule, deleteRule } from '../services/rules';

// Discord emoji list for auto rules
const DISCORD_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
  '👍', '👎', '❤️', '🔥', '🎉', '🤔', '👀', '💯', '✨', '🌟', '💫', '⭐', '💥', '💣', '💢', '💦', '💨', '💤', '👋', '🤚',
  '🖐️', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '🙏', '💪', '🦾', '🦿'
];

const Rules = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'keyword',
    value: '',
    rule_source: 'custom',
    emoji: '',
    is_active: true,
    priority: 10
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch rules from API
  const fetchRules = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.rule_type = filterType;
      if (filterSource !== 'all') params.rule_source = filterSource;
      
      const rulesData = await getRules(params);
      setRules(rulesData);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      showToast('Failed to load rules: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, [filterType, filterSource]);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getTypeVariant = (type) => {
    switch (type) {
      case 'keyword': return 'success';
      case 'regex': return 'info';
      case 'user': return 'warning';
      case 'content_type': return 'primary';
      default: return 'secondary';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'keyword': return '🔍';
      case 'regex': return '.*';
      case 'user': return '👤';
      case 'content_type': return '📄';
      default: return '⚙️';
    }
  };

  const getSourceVariant = (source) => {
    return source === 'auto' ? 'info' : 'secondary';
  };

  const getDisplayType = (type) => {
    return type.replace('_', ' ');
  };

  const toggleRowExpansion = (ruleId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(ruleId)) {
      newExpandedRows.delete(ruleId);
    } else {
      newExpandedRows.add(ruleId);
    }
    setExpandedRows(newExpandedRows);
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (rule.value && rule.value.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      value: rule.value,
      rule_source: rule.rule_source,
      emoji: rule.rule_metadata?.emoji || '',  // Changed from metadata to rule_metadata
      is_active: rule.is_active,
      priority: rule.priority || 10
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      rule_type: 'keyword',
      value: '',
      rule_source: 'custom',
      emoji: '',
      is_active: true,
      priority: 10
    });
    setShowModal(true);
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (ruleToDelete) {
      try {
        await deleteRule(ruleToDelete.id);
        fetchRules();
        showToast('Rule deleted successfully');
      } catch (error) {
        console.error('Failed to delete rule:', error);
        showToast('Failed to delete rule: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setRuleToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const ruleData = {
        ...formData,
        rule_metadata: formData.rule_source === 'auto' && formData.emoji ? { emoji: formData.emoji } : {}  // Changed from metadata to rule_metadata
      };

      if (editingRule) {
        await updateRule(editingRule.id, ruleData);
        showToast('Rule updated successfully');
      } else {
        await createRule(ruleData);
        showToast('Rule created successfully');
      }
      setShowModal(false);
      fetchRules();
    } catch (error) {
      console.error('Failed to save rule:', error);
      showToast('Failed to save rule: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading rules...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Rules Management</h1>
          <p className="text-muted mb-0">
            Configure reaction rules for automated responses
          </p>
        </div>
        <Button variant="primary" onClick={handleAdd}>
          <Plus size={16} className="me-2" />
          <span className="d-none d-md-inline">Add Rule</span>
          <span className="d-md-none">Add</span>
        </Button>
      </div>

      {/* Info Alert */}
      <Alert variant="info" className="mb-4">
        <AlertTriangle size={20} className="me-2" />
        Rules are processed in priority order. Lower numbers have higher priority.
      </Alert>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Settings size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{rules.length}</h5>
              <p className="text-muted mb-0 small">Total Rules</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Shield size={24} className="text-success mb-2" />
              <h5 className="fw-bold mb-0">{rules.filter(r => r.is_active).length}</h5>
              <p className="text-muted mb-0 small">Active Rules</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-info mb-2" style={{ fontSize: '1.2rem' }}>🤖</div>
              <h5 className="fw-bold mb-0">{rules.filter(r => r.rule_source === 'auto').length}</h5>
              <p className="text-muted mb-0 small">Auto Rules</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-warning mb-2" style={{ fontSize: '1.2rem' }}>👤</div>
              <h5 className="fw-bold mb-0">{rules.filter(r => r.rule_source === 'custom').length}</h5>
              <p className="text-muted mb-0 small">Custom Rules</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={4}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="keyword">Keyword</option>
                <option value="regex">Regex</option>
                <option value="user">User</option>
                <option value="content_type">Content Type</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
              >
                <option value="all">All Sources</option>
                <option value="auto">Auto</option>
                <option value="custom">Custom</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Rules Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Priority</th>
                  <th className="border-0 py-3">Name</th>
                  <th className="border-0 py-3">Type</th>
                  <th className="border-0 py-3">Source</th>
                  <th className="border-0 py-3">Value</th>
                  <th className="border-0 py-3">Emoji</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((rule) => (
                  <tr key={rule.id} style={{ cursor: 'pointer' }}>
                    <td className="px-4 py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <Badge bg="secondary" className="fs-6">
                        {rule.priority || 10}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <div className="fw-medium">{rule.name}</div>
                      <small className="text-muted">ID: {rule.id}</small>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <Badge bg={getTypeVariant(rule.rule_type)} className="d-flex align-items-center">
                        <span className="me-1">{getTypeIcon(rule.rule_type)}</span>
                        {getDisplayType(rule.rule_type)}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <Badge bg={getSourceVariant(rule.rule_source)}>
                        {rule.rule_source}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <code className="small">{rule.value}</code>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      {rule.rule_metadata?.emoji || '-'}  {/* Changed from metadata to rule_metadata */}
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(rule.id)}>
                      <Badge bg={rule.is_active ? 'success' : 'secondary'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(rule)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteClick(rule)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {filteredRules.map((rule) => {
              const isExpanded = expandedRows.has(rule.id);
              
              return (
                <Card key={rule.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(rule.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <Badge bg="secondary" className="me-1">
                            {rule.priority || 10}
                          </Badge>
                          <Badge bg={getTypeVariant(rule.rule_type)} className="me-1">
                            <span className="me-1">{getTypeIcon(rule.rule_type)}</span>
                            {getDisplayType(rule.rule_type)}
                          </Badge>
                          <Badge bg={getSourceVariant(rule.rule_source)}>
                            {rule.rule_source}
                          </Badge>
                        </div>
                        
                        <div className="fw-medium small">{rule.name}</div>
                        <div className="text-muted small">ID: {rule.id}</div>
                        
                        <div className="mt-2">
                          <code className="small">{rule.value}</code>
                        </div>
                        
                        {rule.rule_metadata?.emoji && (
                          <div className="mt-1">
                            <span className="small">Emoji: {rule.rule_metadata.emoji}</span>
                          </div>
                        )}
                        
                        <div className="mt-2">
                          <Badge bg={rule.is_active ? 'success' : 'secondary'}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(rule);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(rule);
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredRules.length === 0 && (
            <div className="text-center py-5 text-muted">
              No rules found. {searchTerm ? 'Try a different search term.' : 'Create your first rule!'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingRule ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Rule
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add New Rule
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Rule Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., React to Giveaways"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="py-2"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Rule Type</Form.Label>
                  <Form.Select 
                    value={formData.rule_type}
                    onChange={(e) => handleInputChange('rule_type', e.target.value)}
                    className="py-2"
                  >
                    <option value="keyword">🔍 Keyword</option>
                    <option value="regex">.* Regex</option>
                    <option value="user">👤 User</option>
                    <option value="content_type">📄 Content Type</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Rule Source</Form.Label>
                  <Form.Select 
                    value={formData.rule_source}
                    onChange={(e) => handleInputChange('rule_source', e.target.value)}
                    className="py-2"
                  >
                    <option value="custom">👤 Custom</option>
                    <option value="auto">🤖 Auto</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Priority</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="10"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 10)}
                    min="1"
                    className="py-2"
                    required
                  />
                  <Form.Text className="text-muted">
                    Lower numbers = higher priority
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select 
                    value={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                    className="py-2"
                  >
                    <option value={true}>✅ Active</option>
                    <option value={false}>❌ Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              {formData.rule_source === 'auto' && (
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Select Emoji</Form.Label>
                    <div className="emoji-picker">
                      {DISCORD_EMOJIS.map(emoji => (
                        <Button
                          key={emoji}
                          variant={formData.emoji === emoji ? 'primary' : 'outline-secondary'}
                          className="emoji-btn m-1"
                          onClick={() => handleInputChange('emoji', emoji)}
                          type="button"
                        >
                          {emoji}
                        </Button>
                      ))}
                    </div>
                    {formData.emoji && (
                      <div className="mt-2">
                        <Badge bg="info">Selected: {formData.emoji}</Badge>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              )}
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    Rule Value
                    <Badge bg="light" text="dark" className="ms-2">
                      {formData.rule_type === 'keyword' ? 'e.g., giveaway' :
                       formData.rule_type === 'regex' ? 'e.g., \\bwin\\b' :
                       formData.rule_type === 'user' ? 'e.g., user123' :
                       formData.rule_type === 'content_type' ? 'e.g., image' : 'Value'}
                    </Badge>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      formData.rule_type === 'keyword' ? 'e.g., giveaway' :
                      formData.rule_type === 'regex' ? 'e.g., \\bwin\\b' :
                      formData.rule_type === 'user' ? 'e.g., user123' :
                      formData.rule_type === 'content_type' ? 'e.g., image' : 'Enter value'
                    }
                    value={formData.value}
                    onChange={(e) => handleInputChange('value', e.target.value)}
                    className="py-2"
                    required
                  />
                  <Form.Text className="text-muted">
                    {formData.rule_type === 'keyword' ? 'Keyword to match' :
                     formData.rule_type === 'regex' ? 'Regular expression pattern' :
                     formData.rule_type === 'user' ? 'User ID or username' :
                     formData.rule_type === 'content_type' ? 'Content type to match' : 'Rule value'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingRule ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingRule ? 'Update Rule' : 'Add Rule'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-danger">
            <AlertTriangle size={24} className="me-2" />
            Delete Rule
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <Trash2 size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold">Are you sure you want to delete this rule?</h5>
            {ruleToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="fw-medium">{ruleToDelete.name}</div>
                <div className="small text-muted">ID: {ruleToDelete.id}</div>
                <Badge bg={getTypeVariant(ruleToDelete.rule_type)} className="mt-2">
                  {getDisplayType(ruleToDelete.rule_type)}
                </Badge>
                <Badge bg={getSourceVariant(ruleToDelete.rule_source)} className="ms-2 mt-2">
                  {ruleToDelete.rule_source}
                </Badge>
                <div className="mt-2">
                  <code className="small">{ruleToDelete.value}</code>
                </div>
                {ruleToDelete.rule_metadata?.emoji && (
                  <div className="mt-2">
                    <span className="small">Emoji: {ruleToDelete.rule_metadata.emoji}</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-muted mt-3">
              This action cannot be undone. The rule will be permanently removed from the system.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={handleDeleteCancel}
            className="px-4"
            size="sm"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            className="px-4"
            size="sm"
          >
            <Trash2 size={16} className="me-2" />
            Delete Rule
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .emoji-picker {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 0.5rem;
        }
        .emoji-btn {
          font-size: 1.2rem;
          width: 40px;
          height: 40px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default Rules;